import React, { useState, useEffect, useContext } from 'react';
import LayoutContext from 'components/contexts/LayoutContext.jsx';
import LoadFromLocalStorage from 'components/layout_and_quote_builder/options/LoadFromLocalStorage.jsx'
import Constants from 'components/layout_and_quote_builder/Constants.jsx'

// Layout dimensions to hide from the dropdown
const HIDDEN_LAYOUT_DIMENSIONS = [
  "2A0 1189mm X 1682mm",
  "4A0 1682mm X 2378mm",
];

// For "Other sizes" group, only show these dimension IDs
const ALLOWED_OTHER_SIZES_IDS = [
  21, // 700mm X 1000mm / 27.5591in X 39.3701in
];

const ChooseLayoutDimensions = ({ index, title, item, hideCustom, publicSetter, defaultValues, smallerSubSetOf }) => {
  const myLayoutContext = useContext(LayoutContext);

  // Get initial primary layout dimension ID
  const getInitialPrimaryLayoutDimensionId = () => {
    // Check if defaultValues explicitly provides layout_dimension_ids
    if (defaultValues && "layout_dimension_ids" in defaultValues) {
      const ids = Array.isArray(defaultValues["layout_dimension_ids"])
        ? defaultValues["layout_dimension_ids"]
        : [defaultValues["layout_dimension_ids"]];
      // If empty array was explicitly provided, return undefined (don't fallback)
      if (Array.isArray(defaultValues["layout_dimension_ids"]) && defaultValues["layout_dimension_ids"].length === 0) {
        return undefined;
      }
      return ids[0] || 21;
    }
    if (defaultValues && defaultValues["layout_dimension_id"]) {
      return defaultValues["layout_dimension_id"];
    }
    if (myLayoutContext.layoutOptions.layout_dimension_ids) {
      return myLayoutContext.layoutOptions.layout_dimension_ids[0] || 21;
    }
    if (myLayoutContext.layoutOptions.layout_dimension_id) {
      return myLayoutContext.layoutOptions.layout_dimension_id;
    }
    // return 21; // Default fallback
  };

  // Get initial alternative layout dimension ID
  const getInitialAlternativeLayoutDimensionId = () => {
    if (defaultValues && defaultValues["layout_dimension_ids"]) {
      const ids = Array.isArray(defaultValues["layout_dimension_ids"])
        ? defaultValues["layout_dimension_ids"]
        : [defaultValues["layout_dimension_ids"]];
      return ids[1] || '';
    }
    if (myLayoutContext.layoutOptions.layout_dimension_ids) {
      return myLayoutContext.layoutOptions.layout_dimension_ids[1] || '';
    }
    return ''; // No alternative by default
  };

  const [primaryLayoutDimensionId, setPrimaryLayoutDimensionId] = useState(getInitialPrimaryLayoutDimensionId());
  const [alternativeLayoutDimensionId, setAlternativeLayoutDimensionId] = useState(getInitialAlternativeLayoutDimensionId());

  const [layout_dimension_filtering_enabled, set_layout_dimension_filtering_enabled] = useState( false );

  const getSmallerSubSetOfValues = () => {
    if(smallerSubSetOf == undefined) { return [ ] }
    const res = [];
    var width = 0
    var height = 0

    for (let [key, values] of Object.entries(myLayoutContext.dimensionGroups)) {
      for(let i=0; i < values.length; i++) {

        if(values[i].id == smallerSubSetOf) {
          width = values[i].width
          height = values[i].height
        }
      }
    }

    return [width, height]
  }

  const correct = (value) => {
    var close = 0.05;
    if( Math.abs( Math.ceil(value) - value ) < close ) { return true }
    if( Math.abs( Math.floor(value) - value ) < close ) { return true }

    return false
  }

  const getSmallerSubSetOfIds = () => {
    if(smallerSubSetOf == undefined) {
      return []
    }

    let smallerSubSetOfValues = getSmallerSubSetOfValues()
    const w = smallerSubSetOfValues[0];
    const h = smallerSubSetOfValues[1];
    var res = []

    for (let [key, values] of Object.entries(myLayoutContext.dimensionGroups)) {
      for(let i=0; i < values.length; i++) {
        let value = values[i];
        let w_part = w / value.width;
        let h_part = h / value.height;
        if( w_part > 0.99 && h_part > 0.99 && correct(w_part) && correct(h_part) ) {
          res.push(value.id)
        }
      }
    }
    return res
  }

  // Check if a layout dimension should be shown based on filtering rules
  const shouldShowLayoutDimension = (value, groupName) => {
    // Hide specific dimensions by description
    if (HIDDEN_LAYOUT_DIMENSIONS.includes(value.description)) {
      return false;
    }

    // For "Other sizes" group, only show allowed dimensions by ID
    if (groupName === "Other sizes") {
      return ALLOWED_OTHER_SIZES_IDS.includes(value.id);
    }

    return true;
  };

  // Get all layout dimension options grouped by groupName
  const getGroupedLayoutDimensionOptions = () => {
    let groupedOptions = {};
    let smallerSubSetOfListIds = getSmallerSubSetOfIds();

    for (let [groupName, values] of Object.entries(myLayoutContext.dimensionGroups)) {
      let sortedValues = [...values].sort((a, b) => a.description.localeCompare(b.description));
      if (groupName === "Other sizes") {
        sortedValues = sortedValues.reverse();
      }

      sortedValues.forEach((value) => {
        // Apply visibility filtering
        if (!shouldShowLayoutDimension(value, groupName)) {
          return;
        }

        if (smallerSubSetOfListIds.length > 0) {
          if (smallerSubSetOfListIds.includes(value.id)) {
            if (!groupedOptions[groupName]) {
              groupedOptions[groupName] = [];
            }
            groupedOptions[groupName].push({ ...value, groupName });
          }
        } else {
          if (!groupedOptions[groupName]) {
            groupedOptions[groupName] = [];
          }
          groupedOptions[groupName].push({ ...value, groupName });
        }
      });
    }

    return groupedOptions;
  };

  // Compute layout_dimension_ids from primary and alternative for backwards compatibility
  const getLayoutDimensionIds = () => {
    const parseLayoutDimensionId = (id) => {
      // Keep custom dimension IDs as strings (e.g., "custom_123")
      if (typeof id === 'string' && id.startsWith('custom_')) {
        return id;
      }
      return parseInt(id, 10);
    };

    const ids = [parseLayoutDimensionId(primaryLayoutDimensionId)];
    if (alternativeLayoutDimensionId) {
      ids.push(parseLayoutDimensionId(alternativeLayoutDimensionId));
    }
    return ids;
  };

  const layout_dimension_unit = myLayoutContext.layoutOptions.layout_dimension_unit;
  const getPublicData = () => {
    return {
      layout_dimension_ids: getLayoutDimensionIds(),
      layout_dimension_unit,
      layout_dimension_filtering_enabled,
    }
  }

  const setPublicData = () => { publicSetter( () => getPublicData() ) }
  const dimensionIdsSetter = (value) => {
    const parseId = (id) => {
      // Keep custom dimension IDs as strings
      if (typeof id === 'string' && id.startsWith('custom_')) {
        return id;
      }
      return parseInt(id, 10);
    };

    const isValidId = (id) => {
      if (typeof id === 'string' && id.startsWith('custom_')) {
        return true;
      }
      return id > 0;
    };

    if (Array.isArray(value)) {
      const ids = value.map(v => parseId(v)).filter(v => isValidId(v));
      if (ids.length > 0) {
        setPrimaryLayoutDimensionId(ids[0]);
      }
      if (ids.length > 1) {
        setAlternativeLayoutDimensionId(ids[1]);
      } else {
        setAlternativeLayoutDimensionId('');
      }
    } else if (isValidId(parseId(value))) {
      setPrimaryLayoutDimensionId(parseId(value));
      setAlternativeLayoutDimensionId('');
    }
  }

  useEffect(() => LoadFromLocalStorage(item, 'layout_dimension_ids', dimensionIdsSetter), []);

  // Sync state with defaultValues.layout_dimension_ids on mount
  useEffect(() => {
    if (defaultValues && defaultValues["layout_dimension_ids"]) {
      const ids = Array.isArray(defaultValues["layout_dimension_ids"])
        ? defaultValues["layout_dimension_ids"]
        : [defaultValues["layout_dimension_ids"]];

      if (ids.length > 0) {
        setPrimaryLayoutDimensionId(ids[0]);
      }
      if (ids.length > 1) {
        setAlternativeLayoutDimensionId(ids[1]);
      }
    }
  }, []);

  if(publicSetter != undefined) {
    useEffect( setPublicData, [
      primaryLayoutDimensionId,
      alternativeLayoutDimensionId,
      layout_dimension_unit,
      layout_dimension_filtering_enabled,
    ])
  }

  useEffect( () => {
    emitEvent(Constants.layout_dimension_changed, {
      index: index,
    })
  }, [primaryLayoutDimensionId, alternativeLayoutDimensionId])

  // Render a standard select dropdown with optgroups
  const renderDimensionSelect = (selectedValue, onChange, label, allowEmpty = false) => {
    const groupedOptions = getGroupedLayoutDimensionOptions();
    if(primaryLayoutDimensionId == undefined) {
      return <input type="hidden" />
    }

    return (
      <div style={{display: 'flex', alignItems: 'center', gap: '10px', justifyContent: 'flex-end'}}>
        <span style={{whiteSpace: 'nowrap'}}>{label}</span>
        <select
          className="form-control"
          value={selectedValue}
          onChange={(e) => onChange(e.target.value)}
          disabled={window.user_quote_finalised}
          style={{
            cursor: window.user_quote_finalised ? 'not-allowed' : 'pointer',
            backgroundColor: window.user_quote_finalised ? '#e9ecef' : 'white',
            width: 'auto',
            minWidth: '200px',
          }}
        >
          {allowEmpty && <option value="">-- None --</option>}
          {Object.entries(groupedOptions).map(([groupName, options]) => (
            <optgroup key={groupName} label={groupName}>
              {options.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.description}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
      </div>
    );
  }

  // Check if layout dimension IDs are available
  const hasLayoutDimensions = defaultValues &&
    Array.isArray(defaultValues["layout_dimension_ids"]) &&
    defaultValues["layout_dimension_ids"].length > 0;

  return (<>
  <div >

    <input type="hidden" value={layout_dimension_unit} name="layout_dimension_unit"/>
    <table className="table  table-bordered">
      <tbody>
        {hasLayoutDimensions && (
          <tr>
            <td colSpan="3" style={{textAlign: 'right'}}>
              <div style={{display: 'flex', justifyContent: 'flex-end', gap: '20px'}}>
                {renderDimensionSelect(
                  primaryLayoutDimensionId,
                  setPrimaryLayoutDimensionId,
                  title != undefined ? title : window.wording_layouts_choose_dimensions
                )}
                {renderDimensionSelect(
                  alternativeLayoutDimensionId,
                  setAlternativeLayoutDimensionId,
                  t('quote_builder.alternative_material_dimension'),
                  true
                )}
              </div>
            </td>
          </tr>
        )}

        <tr>
          <td colSpan="3" style={{paddingTop: '10px', paddingBottom: '10px'}}>
            <label style={{display: 'flex', alignItems: 'center', cursor: 'pointer'}}>
              <input
                type="checkbox"
                checked={layout_dimension_filtering_enabled}
                onChange={(e) => set_layout_dimension_filtering_enabled(e.target.checked)}
                style={{marginRight: '8px'}}
              />
              <span>{t('quote_builder.solution_filtering')}</span>
            </label>
          </td>
        </tr>

      </tbody>
    </table>

    </div>
    <div style={{'clear': 'both' }} ></div>
  </>)
}

export default ChooseLayoutDimensions;
